import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, KeyRound, Lock, Mail, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store';
import AuthShell from '../../components/auth/AuthShell';
import { getEmployerOnboardingPath } from '../../utils/employerOnboarding';

const inputBase =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

function LoginPage() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loginChallenge, setLoginChallenge] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, verifyLoginEmail, resendLoginEmailVerification, loading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionNoticeShownRef = useRef(false);
  const logoutReason = location.state?.reason;
  const noticeShown = location.state?.noticeShown;

  useEffect(() => {
    if (
      logoutReason === 'idle_timeout' &&
      !noticeShown &&
      !sessionNoticeShownRef.current
    ) {
      sessionNoticeShownRef.current = true;
      toast.error('Session expired after 30 minutes of inactivity.');
    }
  }, [logoutReason, noticeShown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const result = loginChallenge
      ? await verifyLoginEmail({
        email: loginChallenge.email,
        challengeId: loginChallenge.challengeId,
        code: verificationCode.trim()
      })
      : await login({
        email: formData.email.trim(),
        password: formData.password
      });

    if (!result.success) {
      toast.error(result.error || 'Unable to sign in with those credentials.');
      return;
    }

    if (result.requiresLoginEmailVerification) {
      setLoginChallenge({
        email: result.email || formData.email.trim(),
        challengeId: result.challengeId,
        expiresInMinutes: result.expiresInMinutes
      });
      setVerificationCode('');
      toast.success('Check your email for the sign-in code.');
      return;
    }

    toast.success('Signed in successfully.');
    const nextPath = getEmployerOnboardingPath(result.user, result.user?.verificationStatus);

    navigate(nextPath, { replace: true });
  };

  const handleResendCode = async () => {
    if (!loginChallenge || loading) return;

    const result = await resendLoginEmailVerification({
      email: loginChallenge.email,
      challengeId: loginChallenge.challengeId
    });

    if (!result.success) {
      toast.error(result.error || 'Could not resend the sign-in code.');
      return;
    }

    setLoginChallenge({
      email: result.email || loginChallenge.email,
      challengeId: result.challengeId || loginChallenge.challengeId,
      expiresInMinutes: result.expiresInMinutes || loginChallenge.expiresInMinutes
    });
    setVerificationCode('');
    toast.success('A new sign-in code was sent.');
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in with your employer account to continue."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {!loginChallenge ? (
          <>
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                Work email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${inputBase} pl-10`}
                  placeholder="you@company.com"
                  autoFocus
                  required
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-sky-700 hover:text-sky-800">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`${inputBase} pl-10 pr-10`}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-3.5 text-slate-400 transition hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-sky-100 bg-[#eef9ff] px-4 py-3 text-sm text-slate-700">
              Enter the 6 digit code sent to <span className="font-semibold text-[#0b1f3a]">{loginChallenge.email}</span>.
            </div>

            <div>
              <label htmlFor="verificationCode" className="mb-2 block text-sm font-medium text-slate-700">
                Sign-in code
              </label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputBase} pl-10 tracking-[0.35em]`}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || (loginChallenge && verificationCode.length !== 6)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Signing in...' : loginChallenge ? 'Verify and sign in' : 'Sign in'}
          {!loading && <ArrowRight className="h-4 w-4" />}
        </button>

        {loginChallenge && (
          <div className="flex flex-col gap-3 text-center text-sm sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 font-medium text-sky-700 transition hover:text-sky-800 disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              Resend code
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginChallenge(null);
                setVerificationCode('');
              }}
              className="font-medium text-slate-500 transition hover:text-[#0b1f3a]"
            >
              Use a different account
            </button>
          </div>
        )}
      </form>

      <div className="mt-6 border-t border-slate-200 pt-5 text-center text-sm text-slate-600">
        New to Sanspath?{' '}
        <Link to="/register" className="font-medium text-sky-700 hover:text-sky-800">
          Create an employer account
        </Link>
      </div>
    </AuthShell>
  );
}

export default LoginPage;
